/**
 * 纵横演武场共享领域模型。
 *
 * 本文件是 SPEC.md §3.3 / §6 / §7 中所有业务数据结构的单一事实来源。
 * 纯类型 + 少量常量，不依赖 DOM / Node 专有 API，可被教师端、辩手端、
 * 信令 Worker 与测试共同引用。
 */

/* ------------------------------------------------------------------ */
/* 基础标识与角色                                                      */
/* ------------------------------------------------------------------ */

export type Side = 'AFF' | 'NEG';
export type Seat = 1 | 2 | 3 | 4;
export type ParticipantRole = 'TEACHER' | 'DEBATER';

export const SIDES: readonly Side[] = ['AFF', 'NEG'] as const;
export const SEATS: readonly Seat[] = [1, 2, 3, 4] as const;

export function oppositeSide(side: Side): Side {
  return side === 'AFF' ? 'NEG' : 'AFF';
}

export function sideLabel(side: Side): string {
  return side === 'AFF' ? '正方' : '反方';
}

export interface Participant {
  /** 房间内唯一 id（客户端生成，nanoid 风格） */
  id: string;
  name: string;
  role: ParticipantRole;
  /** 辩手所属阵营；教师可为空 */
  side?: Side;
  /** 辩位 1~4；教师可为空 */
  seat?: Seat;
  joinedAt: number;
}

/* ------------------------------------------------------------------ */
/* 纵横杯 16 环节赛制（改良奥瑞冈制）                                   */
/* ------------------------------------------------------------------ */

export type StageKind = 'CONSTRUCTIVE' | 'CROSS_EXAM' | 'DUEL' | 'FINAL_REBUTTAL';
export type ClockMode = 'SINGLE' | 'CHESS';
export type CardSlot = 'CONSTRUCTIVE' | 'CROSS_EXAM' | 'SANDBOX';
export type MatchPhase = 'LOBBY' | 'RUNNING' | 'STAGE_ENDED' | 'PAUSED' | 'FINISHED';

export interface StageDefinition {
  /** 1 ~ 16，对应纵横杯环节序号 */
  index: number;
  id: string;
  name: string;
  shortName: string;
  kind: StageKind;
  /** 本环节主导/发言方 */
  speakerSide: Side;
  speakerRole: string;
  clockMode: ClockMode;
  /** 双方可用发言预算（秒）。SINGLE 模式下非发言方为 0。 */
  budgets: Record<Side, number>;
  /** 双表 Chess Clock 首先运行的阵营 */
  startSide: Side;
  cardSlot: CardSlot | null;
  /** 双盲发牌配对键：一个配对在「承诺阶段」同时承诺两张牌 */
  pairId: string | null;
  /** 开表前的静默读卡倒计时（秒），0 表示无读卡期 */
  prepSec: number;
  /** 是否允许 Space 切换发言权 */
  allowToggle: boolean;
}

export const STAGE_COUNT = 16;

/* ------------------------------------------------------------------ */
/* 双盲卡片（SPEC §3.3）                                                */
/* ------------------------------------------------------------------ */

export type DebateArchetype =
  | 'A1_ELITE_SPLIT'
  | 'A2_COGNITIVE_OFFLOAD'
  | 'A3_ECONOMIC_DEVALUATION'
  | 'N1_ONTOLOGY_CARRIER'
  | 'N2_HALLUCINATION_RISK'
  | 'N3_COGNITIVE_LEVER';

export const ARCHETYPE_LABEL: Record<DebateArchetype, string> = {
  A1_ELITE_SPLIT: '精英分层·能力分流',
  A2_COGNITIVE_OFFLOAD: '认知卸载·用进废退',
  A3_ECONOMIC_DEVALUATION: '知识经济·价值重估',
  N1_ONTOLOGY_CARRIER: '本体承载·知识仍是主体',
  N2_HALLUCINATION_RISK: '幻觉风险·核验权不可让渡',
  N3_COGNITIVE_LEVER: '认知杠杆·工具放大而非替代',
};

export interface ForbiddenMisquote {
  /** 正则或关键词组合（JavaScript RegExp 字符串），用于确定性抓错 */
  pattern: string;
  /** 命中后的纠错口径提示 */
  correctionAlert: string;
}

export interface MandatoryEvidence {
  id: string;
  /** 例："港大吴延晖(2026) 26,811人追踪" */
  shortTitle: string;
  /** 例："81%学生选择作业外包，平时效率涨18%，闭卷中考跌24%" */
  exactQuoteOrStats: string;
  /** 供确定性核验引擎匹配：["26811", "81%", "外包"] */
  mustIncludeKeywords: string[];
  /** 供断言引擎抓错 */
  forbiddenMisquotes: ForbiddenMisquote[];
  /** 出处（权威文献 / 判例 / 期刊） */
  source?: string;
  /** 用于检索的主题标签 */
  tags?: string[];
}

export interface ConstructiveStrategyCard {
  cardId: string;
  /** 对应纵横杯 1~16 环节序号 */
  stageIndex: number;
  /** 例："正方二辩" */
  targetRole: string;
  archetype: DebateArchetype;
  title: string;
  /** ≤2 句话的核心战略指令 */
  coreMission: string;
  /** 供辩手拼装的精炼语料块（每块不超过 3 行可视高度） */
  modularScriptBlocks: {
    headerAnchor: string;
    bodyModule: string;
    footerTrap: string;
  };
  mandatoryEvidences: MandatoryEvidence[];
  /** 折叠手风琴组件，按 Tab / 点击展开 */
  hiddenCounterTrap: {
    triggerCondition: string;
    counterResponse: string;
  };
  /** 卡片在 UI 上需要高亮的胶囊标签 */
  pillTags?: string[];
}

export interface CrossExQuestionChain {
  /** 第一刀：锁前提 */
  step1LockPremise: string;
  /** 第二刀：逼两难 */
  step2ForceDilemma: string;
  /** 第三刀：夺判准 */
  step3Crystallize: string;
}

export interface CrossExTaskCard {
  cardId: string;
  stageIndex: number;
  targetRole: string;
  mode: 'TARGETED_COUNTER' | 'BLIND_RECON';
  /** BLIND_RECON 模式下必须为 undefined */
  targetOpponentArchetype?: DebateArchetype;
  questionChain: CrossExQuestionChain;
  /** 本轮绝对不能踩的逻辑雷区提醒 */
  tabooWarning: string;
  /** 情报提示（定向克制模式给出流派倾向；全盲模式给出侦察建议） */
  intelHint?: string;
}

/* ------------------------------------------------------------------ */
/* 1v1 对辩随机沙盘                                                     */
/* ------------------------------------------------------------------ */

export type SandboxTopic =
  | 'EVIDENCE_OWNERSHIP'
  | 'AI_HALLUCINATION'
  | 'HISTORICAL_ANALOGY';

export const SANDBOX_LABEL: Record<SandboxTopic, string> = {
  EVIDENCE_OWNERSHIP: '实证数据归属权',
  AI_HALLUCINATION: 'AI 幻觉与核验权',
  HISTORICAL_ANALOGY: '历史类比与矿渣提纯',
};

export interface SandboxTurn {
  /** 第 1/2/3 回合 */
  turn: 1 | 2 | 3;
  header: string;
  keyClaim: string;
  /** 引用 evidenceLibrary 中证据 id */
  evidenceIds: string[];
  /** 预判对方回合的拆招要点 */
  counterToOpponent?: string;
}

export interface SandboxPerspective {
  /** 例："正方视角：知识未被替代，只是被重新定价" */
  label: string;
  /** 3 回合推进大纲 */
  threeTurnOutline: SandboxTurn[];
  /** 己方必打数据（引用 evidence id，供断言引擎勾选） */
  mustHitEvidenceIds: string[];
}

export interface SandboxCard {
  cardId: string;
  title: string;
  topic: SandboxTopic;
  /** 沙盘中双方共同争夺的焦点问题 */
  crossfireQuestion: string;
  perspectives: Record<Side, SandboxPerspective>;
}

/* ------------------------------------------------------------------ */
/* DAG 攻防树（React Flow 渲染）                                        */
/* ------------------------------------------------------------------ */

export type DagNodeKind = 'PREMISE' | 'EVIDENCE' | 'ATTACK' | 'COUNTER' | 'WEIGH';
export type DagEdgeKind = 'SUPPORTS' | 'ATTACKS' | 'BRANCHES';

export interface DagNode {
  id: string;
  side: Side;
  kind: DagNodeKind;
  label: string;
  summary: string;
  /** 该节点激活的环节范围 [from, to] */
  stageRange: [number, number];
  archetype?: DebateArchetype;
  evidenceIds: string[];
  /** 预置力导向布局坐标（教师端 React Flow） */
  position?: { x: number; y: number };
}

export interface DagEdge {
  id: string;
  source: string;
  target: string;
  kind: DagEdgeKind;
  label?: string;
}

export interface DebateDag {
  nodes: DagNode[];
  edges: DagEdge[];
}

/* ------------------------------------------------------------------ */
/* 题库数据包                                                           */
/* ------------------------------------------------------------------ */

export interface SideBrief {
  stance: string;
  value: string;
  definition: string;
  coreClashes: string[];
}

export interface CoachRubric {
  dimensions: { id: string; label: string; weight: number; description: string }[];
  /** 跟场打分快捷键的默认分值 */
  defaultScores: number[];
}

export interface MotionPackage {
  motionId: string;
  motion: string;
  title: string;
  version: string;
  sides: Record<Side, SideBrief>;
  stages: StageDefinition[];
  constructiveCards: ConstructiveStrategyCard[];
  crossExCards: CrossExTaskCard[];
  sandboxCards: SandboxCard[];
  /** 全量权威论据库（供 MiniSearch / 断言引擎 / 卡片引用） */
  evidenceLibrary: MandatoryEvidence[];
  dag: DebateDag;
  coachRubric: CoachRubric;
  /** 教练习题/赛后复盘提示 */
  coachingNotes?: string[];
}

/* ------------------------------------------------------------------ */
/* 双盲发牌：Commit-Reveal                                             */
/* ------------------------------------------------------------------ */

export type DealtCard =
  | { kind: 'CONSTRUCTIVE'; card: ConstructiveStrategyCard }
  | { kind: 'CROSS_EXAM'; card: CrossExTaskCard }
  | { kind: 'SANDBOX'; card: SandboxCard; perspective: Side };

export interface Commitment {
  hash: string;
  stageIndex: number;
  pairId: string;
  createdAt: number;
}

export interface RevealedCard {
  pairId: string;
  stageIndex: number;
  side: Side;
  salt: string;
  card: DealtCard;
  hash: string;
  revealedAt: number;
}

export interface PairDealPlan {
  pairId: string;
  /** 配对包含的环节序号（1~2 个） */
  stageIndices: number[];
  /** 承担「申论方」的阵营（若是沙盘，则为 null） */
  constructiveSide: Side | null;
  /** 沙盘配对（1v1 对辩） */
  sandbox: boolean;
}

/* ------------------------------------------------------------------ */
/* 端侧确定性核验引擎                                                    */
/* ------------------------------------------------------------------ */

export type AssertionSeverity = 'INFO' | 'WARN' | 'VIOLATION';

export interface AssertionAlert {
  id: string;
  /** 触发扫描的阵营（说话人） */
  side: Side;
  cardId?: string;
  evidenceId?: string;
  severity: AssertionSeverity;
  /** 例："数据张冠李戴：把 81% 外包率说成了 50%" */
  message: string;
  /** 命中的原文片段 */
  snippet: string;
  matchedText: string;
  stageIndex: number;
  at: number;
}

export interface EvidenceHit {
  evidenceId: string;
  keywords: string[];
  snippet: string;
  at: number;
  side: Side;
}

/* ------------------------------------------------------------------ */
/* 房间公开状态（Yjs 文档的序列化视图）                                  */
/* ------------------------------------------------------------------ */

export interface ClockSnapshot {
  /** 双方剩余毫秒 */
  remainingMs: Record<Side, number>;
  /** 当前计时中的阵营；null 表示暂停/未开表 */
  runningSide: Side | null;
  /** runningSide 开始计时的服务器 epoch 毫秒 */
  runningSince: number | null;
  /** 该快照生成的服务器 epoch 毫秒 */
  updatedAt: number;
}

export interface RoomAnnotation {
  id: string;
  authorId: string;
  authorName: string;
  text: string;
  at: number;
  stageIndex: number;
}

export interface RoomSnapshot {
  roomId: string;
  phase: MatchPhase;
  motionId: string;
  /** 0 = 尚未开始；1~16 = 当前环节 */
  stageIndex: number;
  /** 当前环节内的子状态 */
  stagePhase: 'PREP' | 'SPEAKING' | 'ENDED';
  /** 读卡倒计时截止的服务器 epoch 毫秒（PREP 时有效） */
  prepDeadline: number | null;
  clock: ClockSnapshot;
  /** 已完成的环节序号 */
  completedStages: number[];
  /** pairId -> 双方承诺（只公开 hash） */
  commitments: Record<string, Partial<Record<Side, Commitment>>>;
  /** 已揭示的配对 */
  reveals: RevealedCard[];
  /** 已勾选完成的必打论据：side -> evidenceId[] */
  manualEvidenceChecks: Record<Side, string[]>;
  alerts: AssertionAlert[];
  annotations: RoomAnnotation[];
  /** 教师自定义的环节剩余时间覆盖（调试/训练用） */
  stageOverrides: Record<number, Partial<Record<Side, number>>>;
  startedAt: number | null;
  finishedAt: number | null;
  /** 事件审计流（用于赛后战报与断线恢复） */
  eventLog: RoomEvent[];
}

export type RoomEventType =
  | 'ROOM_CREATED'
  | 'PARTICIPANT_JOINED'
  | 'PARTICIPANT_LEFT'
  | 'MATCH_STARTED'
  | 'STAGE_STARTED'
  | 'CARD_DEALT'
  | 'CLOCK_TOGGLED'
  | 'CLOCK_EXHAUSTED'
  | 'STAGE_ENDED'
  | 'CARDS_REVEALED'
  | 'ASSERTION_ALERT'
  | 'EVIDENCE_CHECKED'
  | 'ANNOTATION_ADDED'
  | 'MATCH_FINISHED';

export interface RoomEvent {
  id: string;
  type: RoomEventType;
  stageIndex: number;
  at: number;
  /** 事件涉及阵营（可选） */
  side?: Side;
  payload?: Record<string, unknown>;
}

/* ------------------------------------------------------------------ */
/* 赛后战报                                                            */
/* ------------------------------------------------------------------ */

export interface DebaterScore {
  participantId: string;
  name: string;
  side: Side;
  seat?: Seat;
  /** 各维度 1~5 分 */
  scores: Record<string, number>;
  comment: string;
}

export interface MatchReport {
  roomId: string;
  motionId: string;
  motion: string;
  startedAt: number;
  finishedAt: number;
  durationMs: number;
  stageLog: { stageIndex: number; name: string; at: number }[];
  evidenceCoverage: {
    side: Side;
    required: number;
    hit: number;
    evidenceIds: string[];
  }[];
  assertionAlerts: AssertionAlert[];
  revealVerification: {
    pairId: string;
    verified: boolean;
    hash: string;
  }[];
  annotations: RoomAnnotation[];
  scores: DebaterScore[];
  speechStats?: {
    side: Side;
    speakingMs: number;
    pauses: number;
    interruptions: number;
    wordsPerMinute?: number;
  }[];
}
